import React, { useState, useEffect } from 'react';
import { Users, Package, Euro, TrendingUp, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { adminApi } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total utilisateurs', value: stats?.total_users || 0, icon: Users, color: 'bg-blue-100 text-blue-600' },
    { label: 'Expéditeurs', value: stats?.total_senders || 0, icon: Users, color: 'bg-green-100 text-green-600' },
    { label: 'Transporteurs', value: stats?.total_carriers || 0, icon: Users, color: 'bg-yellow-100 text-yellow-600' },
    { label: 'Total missions', value: stats?.total_parcels || 0, icon: Package, color: 'bg-purple-100 text-purple-600' },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Tableau de bord Admin
        </h1>
        <p className="text-slate-600 mt-1">Vue d'ensemble de la plateforme COLISFLASH</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="border-slate-200" data-testid={`stat-card-${index}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Revenue Card */}
      <Card className="border-slate-200 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 uppercase tracking-wide font-medium mb-2">Commission totale gagnée</p>
              <p className="text-5xl font-bold">{formatPrice(stats?.total_commission_earned || 0)}</p>
              <p className="text-orange-200 mt-2">15% sur chaque livraison</p>
            </div>
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <Euro className="w-10 h-10" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-500" />
            Répartition des missions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-blue-600">{stats?.parcels_by_status?.created || 0}</p>
              <p className="text-sm text-blue-800 font-medium uppercase mt-1">En attente</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats?.parcels_by_status?.delivered || 0}</p>
              <p className="text-sm text-yellow-800 font-medium uppercase mt-1">Livrées</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-600">{stats?.parcels_by_status?.paid || 0}</p>
              <p className="text-sm text-green-800 font-medium uppercase mt-1">Payées</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
